import { Aria } from "../util/aria/aria.util";
import { S25Util } from "../util/s25-util";

export const ARIA_LIVE_POLITE_ID = "AriaLivePolite";
export const ARIA_LIVE_ASSERTIVE_ID = "AriaLiveAssertive";

export class AriaLive {
    private static politeRegion: HTMLElement | null = null;
    private static assertiveRegion: HTMLElement | null = null;
    private static politeQueue: string[] = [];
    private static assertiveTimeout: ReturnType<typeof setTimeout> | undefined;
    private static draining = false;

    /** Gap between polite announcements after a message has been filled. */
    private static readonly DRAIN_MS = 150;
    /**
     * Delay between clearing a region and writing the next message.
     * VoiceOver/Safari often misses announcements when clear+fill happen in the same tick;
     * 100-150ms is the delay WordPress a11y found reliable.
     */
    private static readonly FILL_MS = 150;

    /** Create empty live regions early so AT can register them before the first announcement. */
    public static init() {
        AriaLive.getPoliteRegion();
        AriaLive.getAssertiveRegion();
    }

    public static announce(announcement: string, assertive?: boolean): Promise<void> {
        if (!announcement) return Promise.resolve();

        if (assertive) {
            clearTimeout(this.assertiveTimeout);
            const update = AriaLive.updateRegion(AriaLive.getAssertiveRegion(), announcement);
            this.assertiveTimeout = update.timeout;
            return update.promise;
        }

        AriaLive.politeQueue.push(announcement);
        return AriaLive.drain();
    }

    private static getPoliteRegion(): HTMLElement {
        if (AriaLive.politeRegion) return AriaLive.politeRegion;
        AriaLive.politeRegion = AriaLive.createRegion(ARIA_LIVE_POLITE_ID, "polite", "status");
        return AriaLive.politeRegion;
    }

    private static getAssertiveRegion(): HTMLElement {
        if (AriaLive.assertiveRegion) return AriaLive.assertiveRegion;
        AriaLive.assertiveRegion = AriaLive.createRegion(ARIA_LIVE_ASSERTIVE_ID, "assertive", "alert");
        return AriaLive.assertiveRegion;
    }

    private static createRegion(id: string, live: "polite" | "assertive", role: string): HTMLElement {
        const existing = document.getElementById(id);
        if (existing) {
            Aria.keepVisible(existing);
            return existing;
        }

        const region = document.createElement("div");
        region.id = id;
        region.setAttribute("aria-live", live);
        region.setAttribute("aria-atomic", "true");
        region.setAttribute("role", role);
        // Use clip-based visually-hidden (not opacity:0) so VoiceOver keeps the node in the a11y tree
        region.className = "visuallyhidden";
        document.body.appendChild(region);
        Aria.keepVisible(region);
        return region;
    }

    private static updateRegion(region: HTMLElement, message: string) {
        this.updateRegionPosition(region);
        Aria.setVisible(region);
        region.textContent = "";
        const defer = S25Util.createPromise<void>();
        const timeout = setTimeout(() => {
            Aria.setVisible(region);
            region.textContent = message;
            defer.resolve();
        }, AriaLive.FILL_MS);
        return { promise: defer.promise, timeout };
    }

    private static async drain() {
        if (AriaLive.draining) return;
        AriaLive.draining = true;

        const region = AriaLive.getPoliteRegion();
        while (!!AriaLive.politeQueue.length) {
            const message = AriaLive.politeQueue.shift()!;
            await AriaLive.updateRegion(region, message).promise;
            await S25Util.delay(AriaLive.DRAIN_MS);
        }

        AriaLive.draining = false;
    }

    /** Put live regions back on body (e.g. before a modal is destroyed). */
    public static restoreRegionsToBody(): void {
        for (const region of [AriaLive.politeRegion, AriaLive.assertiveRegion]) {
            if (!region?.parentElement || region.parentElement === document.body) continue;
            document.body.appendChild(region);
        }
    }

    /**
     * Some screen readers will ignore any live regions outside of modals; as a workaround (borrowing from Flutter),
     * we temporarily move the region into the modal so that the screen reader will pick up the announcement
     */
    private static updateRegionPosition(region: HTMLElement) {
        if (!region.isConnected) document.body.appendChild(region);

        // Find the topmost open modal (ng-bootstrap or any custom aria-modal dialog), if any.
        const modals = document.querySelectorAll<HTMLElement>("ngb-modal-window, [aria-modal='true']");
        const modal = modals.length ? modals[modals.length - 1] : null;

        // Move region into or out of the modal
        if (!!modal) {
            const homeParent = region.parentElement;
            const movedIntoModal = !!(modal && homeParent && homeParent !== modal);
            if (movedIntoModal) modal!.appendChild(region);
        } else {
            this.restoreRegionsToBody();
        }
    }
}
